/* eslint-disable no-unsafe-optional-chaining */
import {toJS} from 'mobx';
import {TemplateDataFields} from '../../Agent/ContactForm/EmailEditor/EmailEditor';
import {useAfterSaleCombinationQuote, useCombinationQuote} from '../../Agent/Quotes/useCombinationQuote';
import {TemplateAfterSaleQuoteData} from '../../Agent/types';
import {QuoteHistoryData} from '../mobx/ParameterStore/types';
import Utils from '../Utils';
import {fieldOptions} from '../mobx/ParameterStore/ApprovalXref';

export const fillTemplateData = (
    contactName: string | undefined,
    name: string | undefined,
    coverage: number,
    cashValue: number,
    monthlyPremium: number,
    lifeCoverage: number,
    lifePremium: number,
    termPeriod: number,
    productType: string | undefined,
    carrier: string | undefined,
    illustration?: string,
    managerName?: string,
) => {
    const templateData = {} as any;

    const format = (value: any) => {
        return value && value.toLocaleString('en-US', {style: 'currency', currency: 'USD'});
    };

    let displayCarrier = carrier;
    if (carrier === 'EMC') displayCarrier = 'EMC National Life Company';

    templateData[TemplateDataFields.contactName] = contactName;
    templateData[TemplateDataFields.name] = name;
    templateData[TemplateDataFields.faceAmount] = format(coverage);
    templateData[TemplateDataFields.cashValue] = format(cashValue);
    templateData[TemplateDataFields.monthlyPremium] = format(monthlyPremium);
    templateData[TemplateDataFields.lifeCoverage] = format(lifeCoverage);
    templateData[TemplateDataFields.lifePremium] = format(lifePremium);
    templateData[TemplateDataFields.termPeriod] = termPeriod;
    templateData[TemplateDataFields.productType] = productType;
    templateData[TemplateDataFields.carrier] = displayCarrier;
    templateData[TemplateDataFields.illustration] = illustration;
    templateData[TemplateDataFields.managerName] = managerName;

    return templateData;
};

export const useTemplateData = (singleQuote: QuoteHistoryData | null, combinedQuote: QuoteHistoryData | null) => {
    const [{coverage, monthlyPremium, cashValue, lifeCoverage, lifePremium, termPeriod}] = useCombinationQuote(
        singleQuote,
        combinedQuote,
    );
    const carrier =
        singleQuote?.out.find((q) => [q.field, q.fieldName].includes(TemplateDataFields.carrier))?.value +
        (combinedQuote?.out.find((q) => [q.field, q.fieldName].includes(TemplateDataFields.carrier))?.value || '');

    const illustration = singleQuote?.out.find((q) => q.name === 'Illustration')?.value;

    return fillTemplateData(
        singleQuote?.contactName,
        singleQuote?.name,
        coverage,
        cashValue,
        monthlyPremium,
        lifeCoverage,
        lifePremium,
        termPeriod,
        singleQuote?.productType,
        carrier,
        illustration,
    );
};

export const useAfterSaleTemplateData = (
    name: string,
    firstQuote: TemplateAfterSaleQuoteData,
    secondQuote?: TemplateAfterSaleQuoteData,
) => {
    const [{coverage, monthlyPremium, lifeCoverage, lifePremium, termPeriod}] = useAfterSaleCombinationQuote(
        firstQuote,
        secondQuote,
    );
    const firstCarrier =
        fieldOptions.firstCarrierQuoted.options.find((o) => Utils.MakeVarName(o) === firstQuote.carrierQuoted) ??
        firstQuote.carrierQuoted;
    const secondCarrier =
        fieldOptions.secondCarrierQuoted.options.find((o) => Utils.MakeVarName(o) === secondQuote?.carrierQuoted) ??
        secondQuote?.carrierQuoted;
    const carrier = firstCarrier + (secondCarrier || '');

    return fillTemplateData(
        undefined, // unused in after sale email templates
        name,
        coverage,
        0, // not available from preapproval page currently
        monthlyPremium,
        lifeCoverage,
        lifePremium,
        termPeriod,
        firstQuote.productType,
        carrier,
    );
};
